import { Badge, Button, Card, EmptyState, Group, SegmentedControl, Stack, Table, Text, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useCreateReport, useReports } from "../../api/generated/stockvol";
import type { PageReportOut } from "../../api/generated/model";
import { useSiteInfo } from "../../api/hooks";
import { RequireRole } from "../../auth";
import { act } from "../../lib/actions";
import { IconFileSpreadsheet, IconFileTypePdf, IconFileTypeXls } from "../../components/icons";
import { PageHeader } from "../../components/PageHeader";
import { Section, TableCard } from "../../components/Section";
import { TableSkeleton } from "../../components/Skeletons";
import { formatLocal, localToUtcIso, toLocalInput } from "../../lib/time";

const data = <T,>(r: unknown) => (r as { data: T }).data;

/** Reports: download the XLSX/PDF of any report; operators generate one on demand (M6.6). */
export default function ReportsPage() {
  const site = useSiteInfo();
  const tz = site.data?.timezone ?? "UTC";
  const qc = useQueryClient();
  const reports = useReports({ limit: 100 }, { query: { select: data<PageReportOut>, refetchInterval: 60_000 } });
  const create = useCreateReport();
  const [kind, setKind] = useState<"daily" | "two_hourly">("daily");
  const [date, setDate] = useState(() => formatLocal(Date.now() - 86_400_000, tz, "date"));
  const [at, setAt] = useState(() => {
    const t = new Date();
    t.setUTCMinutes(0, 0, 0);
    t.setUTCHours(t.getUTCHours() - (t.getUTCHours() % 2) - 1); // the last odd UTC hour: :00 local in UB
    return toLocalInput(t, tz);
  });
  return (
    <Stack gap="lg">
      <PageHeader
        title="Reports"
        subtitle="Daily and 2-hourly stockpile reports with volumes, tonnes and changes, in Excel and PDF"
      />
      <RequireRole min="operator">
        <Section title="Generate a report">
        <Group align="flex-end">
          <SegmentedControl
            value={kind}
            onChange={(v) => setKind(v as "daily" | "two_hourly")}
            data={[
              { label: "Daily", value: "daily" },
              { label: "2-hourly", value: "two_hourly" },
            ]}
          />
          {kind === "daily" ? (
            <TextInput type="date" label="Reporting day" value={date} onChange={(e) => setDate(e.currentTarget.value)} />
          ) : (
            <TextInput
              type="datetime-local"
              label={`Boundary (${tz})`}
              value={at}
              onChange={(e) => setAt(e.currentTarget.value)}
            />
          )}
          <Button
            loading={create.isPending}
            onClick={() =>
              void act(
                () =>
                  create.mutateAsync({
                    data: kind === "daily" ? { kind, date } : { kind, at: localToUtcIso(at, tz) },
                  }),
                { done: "Report generated", qc, refresh: ["reports"] },
              )
            }
          >
            Generate
          </Button>
        </Group>
        </Section>
      </RequireRole>
      {reports.isPending ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (reports.data?.items.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            icon={<IconFileSpreadsheet size={30} />}
            title="No reports yet"
            description="The daily report is generated after each reporting day ends."
            py="xl"
          />
        </Card>
      ) : (
        <TableCard title="Reports">
        <Table highlightOnHover verticalSpacing="sm" fz="sm" data-testid="reports-table">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Period</Table.Th>
              <Table.Th>Kind</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Generated</Table.Th>
              <Table.Th>Files</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {reports.data?.items.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>
                  <Text size="sm" fw={600}>
                    {formatLocal(r.period_start, tz)} → {formatLocal(r.period_end, tz, "time")}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="light" color={r.kind === "daily" ? "brand" : "gray"} tt="none">
                    {r.kind === "two_hourly" ? "2-hourly" : r.kind}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" color={r.status === "revised" ? "orange" : "teal"} variant="light">
                    {r.status}
                  </Badge>
                </Table.Td>
                <Table.Td c="dimmed">
                  {formatLocal(r.generated_at, tz)} · {r.generated_by}
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    {r.has_xlsx && (
                      <Button
                        component="a"
                        href={`/api/reports/${r.id}/xlsx`}
                        download
                        data-testid={`xlsx-${r.id}`}
                        size="compact-sm"
                        variant="light"
                        color="teal"
                        leftSection={<IconFileTypeXls size={16} />}
                      >
                        XLSX
                      </Button>
                    )}
                    {r.has_pdf && (
                      <Button
                        component="a"
                        href={`/api/reports/${r.id}/pdf`}
                        download
                        data-testid={`pdf-${r.id}`}
                        size="compact-sm"
                        variant="light"
                        color="red"
                        leftSection={<IconFileTypePdf size={16} />}
                      >
                        PDF
                      </Button>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </TableCard>
      )}
    </Stack>
  );
}
